// Vídeo do produto (YouTube) — funções puras, sem I/O e sem imports "@/...":
// usadas pelo servidor (sync do Olist, página de produto), pelo cliente
// (player com fachada) e direto pelos testes node:test.
//
// Só YouTube é incorporado na página. Qualquer outro link continua guardado
// como veio (media_assets.storage_path), mas não vira player.

/** Id de vídeo do YouTube: 11 caracteres [A-Za-z0-9_-]. */
const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

/** Hosts aceitos (sem "www.", "m.", "music."). */
const YOUTUBE_HOSTS = new Set(["youtube.com", "youtube-nocookie.com", "youtu.be"]);

/** Primeiro segmento de caminho que traz o id logo depois (youtube.com/<prefixo>/<id>). */
const ID_PATH_PREFIXES = new Set(["embed", "shorts", "live", "v", "e"]);

export const MAX_VIDEO_URL_LENGTH = 2_000;

export function isYouTubeVideoId(value: unknown): value is string {
  return typeof value === "string" && YOUTUBE_VIDEO_ID.test(value);
}

function toUrl(raw: string): URL | null {
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`;
  try {
    const url = new URL(candidate);
    return url.protocol === "https:" || url.protocol === "http:" ? url : null;
  } catch {
    return null;
  }
}

function normalizedHost(url: URL): string {
  return url.hostname.toLowerCase().replace(/^(?:www\.|m\.|music\.)/, "");
}

/**
 * Id do vídeo para links do YouTube (watch?v=, youtu.be/, shorts/, embed/,
 * live/, v/), com ou sem "https://"; null para qualquer outro host, link
 * malformado ou id fora do formato.
 */
export function parseYouTubeVideoId(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed.length > MAX_VIDEO_URL_LENGTH) return null;
  const url = toUrl(trimmed);
  if (!url) return null;
  const host = normalizedHost(url);
  if (!YOUTUBE_HOSTS.has(host)) return null;

  const segments = url.pathname.split("/").filter(Boolean);
  let candidate: string | null | undefined;
  if (host === "youtu.be") {
    candidate = segments[0];
  } else if (segments[0] === "watch" || segments.length === 0) {
    candidate = url.searchParams.get("v");
  } else if (ID_PATH_PREFIXES.has(segments[0])) {
    candidate = segments[1];
  }
  return isYouTubeVideoId(candidate) ? candidate : null;
}

/**
 * Link do vídeo como deve ser guardado: texto aparado, até 2.000
 * caracteres; null se vazio ou longo demais. Não exige YouTube (o link cru
 * fica guardado mesmo quando não vira player).
 */
export function normalizeStoredVideoUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed.length > MAX_VIDEO_URL_LENGTH) return null;
  return trimmed;
}

/**
 * Endereço do player incorporado: domínio youtube-nocookie (sem cookies de
 * rastreamento até o play), autoplay (o player só é montado depois do
 * clique), rel=0 (sugestões no fim só do mesmo canal), playsinline (iPhone
 * toca dentro da página, sem abrir em tela cheia).
 */
export function buildYouTubeEmbedUrl(videoId: string): string {
  if (!isYouTubeVideoId(videoId)) throw new Error("YOUTUBE_VIDEO_ID_INVALID");
  return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
}

/** Miniatura pública (480x360) servida pelo i.ytimg.com. */
export function buildYouTubeThumbnailUrl(videoId: string): string {
  if (!isYouTubeVideoId(videoId)) throw new Error("YOUTUBE_VIDEO_ID_INVALID");
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}
